import React, { useState, useMemo } from 'react';
import { Shield, UserCheck, UserX, Search, Eye, FileText, Mail, Fingerprint, Calendar, GraduationCap, FileSignature, ChevronRight, Maximize2, Minimize2, ArrowLeft, X, RotateCcw } from 'lucide-react';
import api from '../api';

const StudentManagement = ({ onClose }) => {
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [viewRejected, setViewRejected] = useState(false);
    const [academicReviewMode, setAcademicReviewMode] = useState(false); // Added academicReviewMode state
    const [observations, setObservations] = useState({});
    const [savingObs, setSavingObs] = useState({});
    
    React.useEffect(() => {
        fetchStudents();
    }, [viewRejected]);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const res = await api.get(`users/students/?rejected=${viewRejected}`);
            setStudents(res.data);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                sessionStorage.removeItem('token');
                window.location.reload(); 
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        await api.post(`users/${id}/approve/`);
        fetchStudents();
    };

    const handleUnapprove = async (id) => {
        if (window.confirm('¿Desea quitar el visto bueno y dejar al alumno como pendiente?')) {
            await api.post(`users/${id}/unapprove/`);
            fetchStudents();
        }
    };

    const handleReject = async (id) => {
        const msg = viewRejected ? '¿Está seguro de que desea restaurar este alumno?' : '¿Está seguro de que desea restringir y marcar como eliminado este alumno?';
        if (window.confirm(msg)) {
            if (viewRejected) {
                await api.post(`users/${id}/approve/`);
            } else {
                await api.post(`users/${id}/reject/`);
            }
            if (selectedStudent?.id === id) setSelectedStudent(null);
            fetchStudents();
        }
    };

    const handleResetPassword = async (id, isTeacher) => {
        const roleName = isTeacher ? 'docente' : 'alumno';
        if (window.confirm(`¿Desea restablecer la contraseña de este ${roleName} a la predeterminada?`)) {
            try {
                const res = await api.post(`users/${id}/reset_any_password/`);
                alert(`${res.data.status}\nNueva contraseña: ${res.data.temp_password}`);
                fetchStudents();
            } catch (err) {
                console.error(err);
                alert('Error al restablecer la contraseña.');
            }
        }
    };

    const handleSaveObservation = async (docId, observationText) => {
        setSavingObs(prev => ({ ...prev, [docId]: true }));
        try {
            await api.patch(`users/documents/${docId}/`, { observation: observationText });
            // Update the local state for the specific document
            setStudents(prevStudents => prevStudents.map(student => {
                if (student.id === selectedStudent.id) {
                    return {
                        ...student,
                        profile: {
                            ...student.profile,
                            documents: student.profile.documents.map(doc => 
                                doc.id === docId ? { ...doc, observation: observationText } : doc
                            )
                        }
                    };
                }
                return student;
            }));
            setSelectedStudent(prev => ({
                ...prev,
                profile: {
                    ...prev.profile,
                    documents: prev.profile.documents.map(doc => 
                        doc.id === docId ? { ...doc, observation: observationText } : doc
                    )
                }
            }));
            setObservations(prev => ({ ...prev, [docId]: undefined })); // Clear pending observation
        } catch (error) {
            console.error("Error saving observation:", error);
            alert("Error al guardar la observación.");
        } finally {
            setSavingObs(prev => ({ ...prev, [docId]: false }));
        }
    };

    // Filtro y Ordenamiento Alfabético
    const filteredStudents = useMemo(() => {
        return [...students]
            .filter(s => {
                const searchLower = searchTerm.toLowerCase();
                const fullName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
                return s.email.toLowerCase().includes(searchLower) || fullName.includes(searchLower);
            })
            .sort((a, b) => {
                const nameA = (a.first_name || a.email).toLowerCase();
                const nameB = (b.first_name || b.email).toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }, [students, searchTerm]);

    const modalStyles = {
        width: isMaximized ? '100vw' : '95%',
        maxWidth: isMaximized ? '100vw' : '1100px',
        height: isMaximized ? '100dvh' : '85vh',
        maxHeight: isMaximized ? 'none' : '85vh',
        borderRadius: isMaximized ? '0' : '20px',
        margin: isMaximized ? '0' : 'auto',
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ padding: isMaximized ? '0' : '1rem' }}>
            <div className="modal-content glassmorphism animate-fade-in d-flex flex-column" 
                 style={modalStyles}
                 onClick={e => e.stopPropagation()}>
                
                {/* Header Estilo Ventana */}
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-secondary border-opacity-25 pb-3">
                    <div className="d-flex align-items-center gap-3">
                        {selectedStudent && (
                            <button 
                                onClick={() => setSelectedStudent(null)} 
                                className="btn btn-sm btn-outline-light d-flex align-items-center gap-1 border-0"
                                title="Volver al listado"
                            >
                                <ArrowLeft size={18} /> <span className="d-none d-md-inline">Volver</span>
                            </button>
                        )}
                        <h3 className="mb-0 d-flex align-items-center section-title" style={{ fontSize: '1.25rem' }}>
                            <Shield className="me-2 text-primary" size={24} /> 
                            {academicReviewMode ? 'Revisión Académica (Visto Bueno)' : (!selectedStudent || !isMaximized ? 'Gestión de Usuarios' : `Ficha: ${selectedStudent.first_name || selectedStudent.email}`)}
                        </h3>
                    </div>
                    
                    <div className="d-flex align-items-center gap-2">
                        <button 
                            onClick={() => setAcademicReviewMode(!academicReviewMode)} 
                            className={`btn btn-sm btn-action ${academicReviewMode ? 'active-info' : 'info'}`} 
                            title={academicReviewMode ? "Salir de Revisión Académica" : "Entrar en Revisión Académica"}
                        >
                            <GraduationCap size={18} />
                        </button>
                        <button 
                            onClick={() => setIsMaximized(!isMaximized)} 
                            className="btn btn-sm btn-action info" 
                            title={isMaximized ? "Restaurar" : "Maximizar"}
                        >
                            {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                        <button
                            type="button"
                            className="btn-close-premium"
                            onClick={onClose}
                            aria-label="Close"
                            title="Cerrar"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="row g-4 flex-grow-1 overflow-hidden">
                    {/* Lista de Alumnos */}
                    <div className={`${selectedStudent && !isMaximized ? 'col-md-5 d-none d-md-flex' : selectedStudent ? 'd-none' : 'col-12'} d-flex flex-column h-100 transition-all`}>
                        <div className="d-flex gap-2 mb-4">
                            <div className="position-relative flex-grow-1 search-container">
                                <Search className="position-absolute top-50 translate-middle-y ms-3 text-primary opacity-75" size={20} />
                                <input 
                                    type="text" 
                                    className="form-control ps-5 premium-search-input" 
                                    placeholder="Buscar alumnos..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button 
                                onClick={() => setViewRejected(!viewRejected)} 
                                className={`btn d-flex align-items-center gap-2 px-3 ${viewRejected ? 'btn-danger bg-opacity-75' : 'btn-outline-secondary'}`}
                                title={viewRejected ? "Volver a alumnos activos" : "Ver alumnos eliminados"}
                            >
                                <UserX size={18} />
                                <span className="d-none d-md-inline">{viewRejected ? 'Borrados' : 'Papelera'}</span>
                            </button>
                        </div>

                        <div className="table-responsive flex-grow-1 border rounded border-secondary border-opacity-25 bg-dark bg-opacity-25">
                            <table className="table premium-table mb-0">
                                <thead className="sticky-top bg-dark">
                                    <tr>
                                        <th>Usuario</th>
                                        <th className="d-none d-lg-table-cell">Rol / Estado</th>
                                        <th className="text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="3" className="text-center p-5">Cargando alumnos...</td></tr>
                                    ) : filteredStudents.length > 0 ? (
                                        filteredStudents.map(s => (
                                            <tr key={s.id} className={selectedStudent?.id === s.id ? 'table-active' : ''}>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-bold text-truncate" style={{ maxWidth: '200px' }}>
                                                            {s.first_name ? `${s.first_name} ${s.last_name || ''}` : 'Sin Nombre'}
                                                        </span>
                                                        <small className="text-muted text-truncate" style={{ maxWidth: '200px' }}>{s.email}</small>
                                                    </div>
                                                </td>
                                                <td className="d-none d-lg-table-cell">
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="badge-premium info py-0 px-2" style={{ fontSize: '0.6rem', alignSelf: 'start' }}>
                                                            {s.role === 'DO' ? 'Docente' : 'Alumno'}
                                                        </span>
                                                        {s.is_rejected ? 
                                                            <span className="badge-premium reject py-1 px-2" style={{ fontSize: '0.7rem' }}>Eliminado</span> :
                                                            s.is_approved ? 
                                                                <span className="badge-premium success py-1 px-2" style={{ fontSize: '0.7rem' }}>Aprobado</span> : 
                                                                <span className="badge-premium warning py-1 px-2" style={{ fontSize: '0.7rem' }}>Pendiente</span>
                                                        }
                                                    </div>
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex justify-content-end gap-1">
                                                        <button 
                                                            onClick={() => setSelectedStudent(selectedStudent?.id === s.id ? null : s)} 
                                                            className={`btn btn-sm btn-action ${selectedStudent?.id === s.id ? 'active-info' : 'info'}`} 
                                                            title={selectedStudent?.id === s.id ? "Cerrar Detalles" : "Ver Detalles"}
                                                        >
                                                            <Eye size={16} />
                                                        </button>
                                                        {s.is_approved && !s.is_rejected && (
                                                            <button 
                                                                onClick={() => handleUnapprove(s.id)} 
                                                                className="btn btn-sm btn-action info" 
                                                                title="Quitar Visto Bueno (Pendiente)"
                                                            >
                                                                <RotateCcw size={16} />
                                                            </button>
                                                        )}
                                                        {!s.is_approved && !s.is_rejected && (
                                                            <button 
                                                                onClick={() => handleApprove(s.id)} 
                                                                className="btn btn-sm btn-action approve" 
                                                                title={academicReviewMode ? "Dar Visto Bueno" : "Aprobar"}
                                                            >
                                                                <UserCheck size={16} />
                                                            </button>
                                                        )}
                                                        <button 
                                                            onClick={() => handleResetPassword(s.id, s.role === 'DO')} 
                                                            className="btn btn-sm btn-action info" 
                                                            title="Restablecer Contraseña"
                                                        >
                                                            <Fingerprint size={16} />
                                                        </button>
                                                        {!academicReviewMode && (
                                                            <button 
                                                                onClick={() => handleReject(s.id)} 
                                                                className={`btn btn-sm btn-action ${viewRejected ? 'approve' : 'reject'}`} 
                                                                title={viewRejected ? "Restaurar" : "Eliminar"}
                                                            >
                                                                {viewRejected ? <UserCheck size={16} /> : <UserX size={16} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="3" className="text-center p-5 text-muted fst-italic">No se encontraron alumnos</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Detalle del Alumno */}
                    {selectedStudent && (
                        <div className={`${isMaximized ? 'col-12' : 'col-md-7'} h-100 overflow-auto animate-slide-in-right`}>
                            <div className="card glassmorphism border-primary border-opacity-25 h-100 overflow-auto">
                                <div className="card-body p-3 p-md-4">
                                    <div className="row g-4 mb-4">
                                        <div className="col-12 d-flex align-items-center gap-3">
                                            <div className="bg-primary bg-opacity-20 p-3 rounded-circle text-primary d-none d-sm-block">
                                                <Fingerprint size={32} />
                                            </div>
                                            <div className="flex-grow-1">
                                                <h4 className="mb-0 fw-bold">{selectedStudent.first_name} {selectedStudent.last_name}</h4>
                                                <div className="text-muted mb-0 d-flex flex-wrap align-items-center gap-x-3 gap-y-1">
                                                    <span className="d-flex align-items-center gap-1"><Mail size={14} /> {selectedStudent.email}</span>
                                                    {selectedStudent.is_approved ? 
                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-0 px-2 small">Aprobado</span> : 
                                                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 py-0 px-2 small">Pendiente</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-4">
                                        <div className="col-sm-6">
                                            <div className="bg-dark bg-opacity-25 p-3 rounded border border-secondary border-opacity-25">
                                                <label className="text-muted small text-uppercase fw-bold mb-1 d-block">DNI</label>
                                                <div className="fw-medium">{selectedStudent.profile?.dni || 'No registrado'}</div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="bg-dark bg-opacity-25 p-3 rounded border border-secondary border-opacity-25">
                                                <label className="text-muted small text-uppercase fw-bold mb-1 d-block">Edad</label>
                                                <div className="fw-medium">{selectedStudent.profile?.age ? `${selectedStudent.profile.age} años` : 'No registrada'}</div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="bg-dark bg-opacity-25 p-3 rounded border border-secondary border-opacity-25">
                                                <label className="text-muted small text-uppercase fw-bold mb-1 d-block d-flex align-items-center gap-2">
                                                    <GraduationCap size={16} /> Últimos Estudios
                                                </label>
                                                <div className="fw-medium">{selectedStudent.profile?.highest_studies || 'No registrados'}</div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="bg-dark bg-opacity-25 p-3 rounded border border-secondary border-opacity-25 fst-italic">
                                                <label className="text-muted small text-uppercase fw-bold mb-1 d-block">Biografía / Notas</label>
                                                {selectedStudent.profile?.biography || 'Sin biografía disponible'}
                                            </div>
                                        </div>
                                    </div>

                                    <h5 className="fw-bold mb-3 d-flex align-items-center">
                                        <FileText className="me-2 text-info" size={20} /> Documentación Adjunta
                                    </h5>
                                    
                                    <div className="row g-3">
                                        {selectedStudent.profile?.documents && selectedStudent.profile.documents.length > 0 ? (
                                            selectedStudent.profile.documents.map(doc => (
                                                <div key={doc.id} className={isMaximized ? 'col-md-6' : 'col-12'}>
                                                    <div className="p-3 rounded bg-dark bg-opacity-25 border border-secondary border-opacity-25 h-100 flex-column overflow-hidden">
                                                        <div className="d-flex align-items-center justify-content-between mb-3 overflow-hidden">
                                                            <div className="d-flex align-items-center overflow-hidden">
                                                                <FileText className="me-3 text-info flex-shrink-0" size={24} />
                                                                <div className="overflow-hidden">
                                                                    <div className="fw-bold text-truncate">{doc.doc_type_display}</div>
                                                                </div>
                                                            </div>
                                                            <a 
                                                                href={doc.file} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-info"
                                                            >
                                                                Ver Documento
                                                            </a>
                                                        </div>

                                                        {academicReviewMode ? (
                                                            <div className="mt-3">
                                                                <label className="small text-muted mb-1 d-block text-uppercase fw-bold">Observaciones Académicas</label>
                                                                <textarea 
                                                                    className="form-control form-control-sm bg-dark bg-opacity-50 text-white border-secondary border-opacity-25"
                                                                    placeholder="Ej: El DNI está borroso..."
                                                                    rows="2"
                                                                    value={observations[doc.id] !== undefined ? observations[doc.id] : (doc.observation || '')}
                                                                    onChange={(e) => setObservations(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                ></textarea>
                                                                <button 
                                                                    onClick={() => handleSaveObservation(doc.id, observations[doc.id] !== undefined ? observations[doc.id] : doc.observation)}
                                                                    disabled={savingObs[doc.id]}
                                                                    className="btn btn-sm btn-primary mt-2 w-100 d-flex justify-content-center align-items-center gap-2"
                                                                >
                                                                    {savingObs[doc.id] ? 'Guardando...' : <><FileSignature size={14} /> Guardar Observación</>}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            doc.observation && (
                                                                <div className="mt-2 p-2 rounded bg-warning bg-opacity-10 border border-warning border-opacity-25">
                                                                    <label className="small text-warning mb-1 d-block text-uppercase fw-bold">Observación del Coordinador Académico</label>
                                                                    <div className="small fst-italic text-white-50">{doc.observation}</div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-12">
                                                <div className="p-4 text-center border border-dashed border-secondary rounded text-muted">
                                                    Este alumno aún no ha subido documentación.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentManagement;
